import {
  Box,
  BoxProps,
  ButtonProps,
  Card,
  CardProps,
  Link,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import { alpha } from "@mui/material/styles";
import { PrimaryButton } from "./PrimaryButton";
import { format } from "date-fns";

const SmallButton = styled(PrimaryButton)<ButtonProps>(({ theme }) => ({
  minHeight: 44,
  fontSize: 14,
  fontWeight: 600,
  [theme.breakpoints.up("sm")]: { minWidth: 150 },
  [theme.breakpoints.down("sm")]: { flex: 1 },
}));

const ValidateButton = styled(PrimaryButton)<ButtonProps>(({ theme }) => ({
  minHeight: 44,
  fontSize: 13,
  fontWeight: 600,
  backgroundColor: "#FFFFFF",
  color: "#014DDF",
  border: "1px solid rgba(1, 77, 223, 0.32)",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#F5F8FF", boxShadow: "none" },
  [theme.breakpoints.up("sm")]: { minWidth: 150 },
  [theme.breakpoints.down("sm")]: { flex: 1 },
}));

const ContainerCard = styled(Card)<CardProps>(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  maxWidth: 860,
  margin: "0 auto 18px",
  padding: 0,
  overflow: "hidden",
  border: "1px solid #E2E8F2",
  borderRadius: 18,
  boxShadow: "0 12px 32px rgba(20, 43, 83, 0.08)",
  transition: "transform 180ms ease, box-shadow 180ms ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 18px 42px rgba(20, 43, 83, 0.13)",
  },
  [theme.breakpoints.up("lg")]: { flexDirection: "row" },
}));

const InfoCardContainer = styled(Box)<BoxProps>(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
  marginTop: 22,
  "& > :first-of-type": { gridColumn: "1 / -1" },
  [theme.breakpoints.up("lg")]: {
    gridTemplateColumns: "minmax(150px, 1.5fr) repeat(2, minmax(86px, 1fr))",
    "& > :first-of-type": { gridColumn: "auto" },
  },
}));

const DownloadBox = styled(Box)<BoxProps>(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  flex: "auto",
  gap: 10,
  padding: 20,
  borderTop: "1px solid #E7ECF4",
  [theme.breakpoints.up("lg")]: {
    flex: "0 0 205px",
    flexDirection: "column",
    borderTop: 0,
    borderLeft: "1px solid #E7ECF4",
  },
  [theme.breakpoints.down("sm")]: {
    padding: 16,
    paddingRight: 76,
  },
}));

const InfoCard = ({ value, label }: { value: string | number; label: string }) => (
  <Box
    sx={{
      minWidth: 0,
      px: 1.5,
      py: 1.1,
      border: "1px solid #E5EAF3",
      borderRadius: 2,
      bgcolor: "#F7F9FC",
    }}
  >
    <Typography
      component="span"
      sx={{
        display: "block",
        color: "text.secondary",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.25,
        overflowWrap: "anywhere",
        color: "text.primary",
        fontSize: 14,
        fontWeight: 600,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const CertificateCard = ({
  data,
  baseColor = "#014DDF",
  onClick,
  isWatana = false,
  onValidateSignature,
}) => (
  <ContainerCard
    className="focus-document-card"
    sx={{
      borderLeft: { lg: `5px solid ${baseColor}` },
      borderTop: { xs: `5px solid ${baseColor}`, lg: "1px solid #E2E8F2" },
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0, p: { xs: 2.5, sm: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Typography
          sx={{
            width: "fit-content",
            px: 1.25,
            py: 0.5,
            borderRadius: 5,
            color: baseColor,
            bgcolor: alpha(baseColor, 0.09),
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          Certificado
        </Typography>
        {data.video_url && (
          <Link
            href={data.video_url}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            Grabaciones <OpenInNewRoundedIcon sx={{ fontSize: 15 }} />
          </Link>
        )}
      </Stack>
      <Typography
        variant="h5"
        sx={{
          mt: 1.5,
          color: "primary.dark",
          fontSize: { xs: 20, sm: 23 },
          fontWeight: 700,
          lineHeight: 1.25,
        }}
      >
        {data.name_course}
      </Typography>
      <InfoCardContainer>
        <InfoCard label="Código" value={data.codigo} />
        <InfoCard
          label="Emisión"
          value={format(new Date(data.created.replace(/-/gi, "/")), "dd/MM/yyyy")}
        />
        <InfoCard label="Nota" value={data.nota} />
      </InfoCardContainer>
    </Box>

    <DownloadBox sx={{ bgcolor: alpha(baseColor, 0.045) }}>
      <Box
        sx={{
          width: { xs: 48, lg: 64 },
          height: { xs: 48, lg: 64 },
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          borderRadius: 3,
          bgcolor: alpha(baseColor, 0.1),
        }}
      >
        <Box component="img" alt="certificado" src="./icons/certificado.png" sx={{ width: 42 }} />
      </Box>
      <Box sx={{ display: "flex", flexDirection: { xs: "row", lg: "column" }, gap: 1, width: "100%" }}>
        <SmallButton
          variant="contained"
          startIcon={<DownloadRoundedIcon />}
          onClick={() => onClick(data)}
        >
          Descargar
        </SmallButton>
        {isWatana && (
          <ValidateButton
            variant="contained"
            startIcon={<VerifiedUserRoundedIcon sx={{ fontSize: 18 }} />}
            onClick={() => onValidateSignature && onValidateSignature(data)}
          >
            Validar firma
          </ValidateButton>
        )}
      </Box>
    </DownloadBox>
  </ContainerCard>
);

export default CertificateCard;
